import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import moment from 'jalali-moment'
import { ManagementAddNewLoan } from 'services/loans/manager'
import { getOneMonthLater } from 'utilities/helper/getOneMonthLater'
import { loanInterestFields } from 'utilities/helper/loanInterest'
import { ITerms } from './steps/StepTerms'
import { applyPattern, SchedulePattern } from './schedulePatterns'

export const MIN_AMOUNT = 5_000
/** the draft endpoint builds one row per month; nobody lends over 10 years */
const MAX_INSTALLMENTS = 120

/**
 * The loan's terms as `draft-installments` and the create call both read
 * them. One builder for both, so the schedule previewed on the terms step is
 * built from exactly the numbers the loan is then created with.
 */
export const loanTermsPayload = (
    moneypoolId: string | undefined,
    amount: string,
    terms: ITerms
) => {
    const feeValue = Number(terms.fee || 0)
    return {
        amount,
        // «lip_ma» is also what a zero-fee loan uses: there is no fee to
        // place, and the backend requires a mode either way
        interest_pay_mode: feeValue > 0 ? terms.feeMode : 'lip_ma',
        ...loanInterestFields(terms.feeType, feeValue),
        number_of_repayments: terms.installments,
        pay_date: moment().locale('en').format('YYYY-MM-DD'),
        due_date: terms.startDate || getOneMonthLater(),
        moneyPool: moneypoolId,
    }
}

/**
 * Everything the backend's draft depends on, as one comparable string. A
 * hand-edited schedule is only valid for the terms it was edited against —
 * change the amount or the count and the edits describe a different loan.
 */
export const scheduleBasis = (amount: string | number, terms: ITerms) =>
    [
        Number(amount || 0),
        Number(terms.installments || 0),
        terms.feeType,
        Number(terms.fee || 0),
        Number(terms.fee || 0) > 0 ? terms.feeMode : 'lip_ma',
        terms.startDate,
    ].join('|')

export interface IResolvedSchedule {
    installments: IInstallment[]
    pattern: SchedulePattern
    /** the manager changed at least one row by hand */
    edited: boolean
}

/**
 * The schedule the loan will actually carry: the manager's hand edits when
 * they still describe these terms, otherwise the chosen pattern applied to
 * the backend's draft.
 */
export const resolveSchedule = (
    draft: IInstallment[],
    amount: string | number,
    terms: ITerms
): IResolvedSchedule => {
    const pattern = terms.pattern ?? 'equal'
    if (
        terms.custom &&
        terms.custom.basis === scheduleBasis(amount, terms) &&
        terms.custom.installments.length === draft.length
    ) {
        return {
            installments: terms.custom.installments,
            pattern,
            edited: true,
        }
    }
    return { installments: applyPattern(draft, pattern), pattern, edited: false }
}

/** true when the create call must carry a schedule other than the server's own */
export const isCustomSchedule = (schedule: IResolvedSchedule) =>
    schedule.edited || schedule.pattern !== 'equal'

const useDebounced = <T,>(value: T, delay: number) => {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(timer)
    }, [value, delay])
    return debounced
}

/**
 * The backend's draft schedule for the terms on screen.
 *
 * Fetched rather than computed: the due dates are Jalali month arithmetic
 * (a due day of 31 in a 30-day month, and so on) that the server already
 * owns. Debounced so typing «۱۲» asks once, not for «۱» and then «۱۲».
 */
export default function useDraftSchedule(
    moneypoolId: string | undefined,
    amount: string,
    terms: ITerms
) {
    const basis = scheduleBasis(amount, terms)
    const debouncedBasis = useDebounced(basis, 400)
    const count = Number(terms.installments || 0)
    const ready =
        !!moneypoolId &&
        Number(amount || 0) >= MIN_AMOUNT &&
        Number.isInteger(count) &&
        count >= 1 &&
        count <= MAX_INSTALLMENTS

    const query = useQuery(
        ['grant-draft-installments', moneypoolId, debouncedBasis],
        () => ManagementAddNewLoan(loanTermsPayload(moneypoolId, amount, terms) as any),
        {
            enabled: ready && debouncedBasis === basis,
            staleTime: 5 * 60_000,
            retry: false,
        }
    )

    const draft =
        ready && debouncedBasis === basis
            ? query.data?.installments
            : undefined

    return {
        draft,
        isLoading: ready && (debouncedBasis !== basis || query.isFetching),
        error: ready ? (query.error as any) : undefined,
        ready,
    }
}
